import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, X } from "lucide-react";
import type { Account, ParamSpec } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { Card, ExchangeMark, Pill, fieldLabel } from "@/components/ui/kit";
import { DynamicForm, buildConfigPayload } from "@/components/DynamicForm";
import { accountKindLabel, accountStatusView, accountUsdBalance, isFuturesAccount } from "@/lib/account";
import { apiKeyPageUrl, missingCredentialMessage, type MarketplacePickerCard } from "@/lib/account-provider";
import { normalizeConfigurableParams } from "@/lib/configurable-params";
import { SERVER_IPS } from "@/lib/server-ip";
import { Ed25519KeyInput } from "@/components/Ed25519KeyInput";
import { isRedacted } from "@/lib/utils";

/* Connect-an-exchange guide (design screen 08), shared by New account and Change keys. */

const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
/** Key/secret fields the self-generated flow replaces. */
const HMAC_FIELDS = new Set(["api_key", "secret", "api_secret", "secret_key"]);

export function exchangeSubtitle(card: MarketplacePickerCard, types: { spot: boolean; futures: boolean }) {
  const kinds = [types.spot && "Spot", types.futures && "Futures"].filter(Boolean).join(" · ");
  return card.staticOptions.network === "DEMO" ? `Demo, no real funds · ${kinds}` : kinds || "Spot";
}

/** Card on a page, plain column inside a dialog. */
function Frame({ frame, children }: { frame: boolean; children: React.ReactNode }) {
  return frame ? <Card className="flex flex-col gap-3.5 p-4">{children}</Card> : <div className="flex flex-col gap-3.5">{children}</div>;
}

/** Copy button for one IP or the whole list. */
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm !border-0 !px-1.5 !py-0.5"
      aria-label={`${label} ${text}`}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(
          () => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          },
          () => setCopied(false)
        );
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied" : label}
    </button>
  );
}

/** Server IPs, each with a copy button, for the exchange's IP whitelist. */
function ServerIps() {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {SERVER_IPS.map((ip) => (
        <span
          key={ip}
          className="inline-flex items-center gap-1.5 rounded-[8px] border border-[var(--border)] bg-[var(--bg-secondary)] py-0.5 pl-2 pr-0.5"
        >
          <span className="num select-all text-[13px] text-[var(--text-primary)]">{ip}</span>
          <CopyButton text={ip} />
        </span>
      ))}
      {SERVER_IPS.length > 1 && <CopyButton text={SERVER_IPS.join(",")} label="Copy all" />}
    </div>
  );
}

/** Guide step (design `.gstep`): numbered row, divider between steps. */
function Step({ n, title, children }: { n: number; title: string; children?: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[28px_minmax(0,1fr)] gap-3 border-t border-[var(--divider)] py-3 first:border-t-0 first:pt-0">
      <span className="num grid h-6 w-6 place-items-center rounded-full bg-[var(--bg-hover)] text-[11px] font-semibold text-[var(--text-muted)]">
        {n}
      </span>
      <div className="min-w-0">
        <b className="text-[13px] font-semibold text-[var(--text-primary)]">{title}</b>
        {children && <div className="mt-[3px] text-xs text-[var(--text-muted)]">{children}</div>}
      </div>
    </div>
  );
}

/** Check / cross icon (design `.ic`). */
function Ic({ ok }: { ok: boolean }) {
  return (
    <span
      className={`grid h-5 w-5 shrink-0 place-items-center rounded-full ${
        ok
          ? "bg-[var(--success-soft)] text-[var(--success)]"
          : "bg-[var(--danger-soft)] text-[var(--danger)]"
      }`}
    >
      {ok ? <Check size={11} strokeWidth={3} /> : <X size={11} strokeWidth={3} />}
    </span>
  );
}

/** Permission chip (design `.perm div`). */
function Perm({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 rounded-[8px] bg-[var(--bg-card-elevated)] px-2 py-1.5 text-xs text-[var(--text-primary)]">
      <Ic ok={ok} />
      {children}
    </div>
  );
}

/** Result row (design `.check`). */
function CheckRow({ ok, title, hint }: { ok: boolean; title: string; hint?: string }) {
  return (
    <div className="grid grid-cols-[22px_minmax(0,1fr)] items-start gap-2.5 border-t border-[var(--divider)] py-2.5 first:border-t-0">
      <Ic ok={ok} />
      <div className="min-w-0">
        <b className="text-[13px] font-medium text-[var(--text-primary)]">{title}</b>
        {hint && <div className="text-xs text-[var(--text-muted)]">{hint}</div>}
      </div>
    </div>
  );
}

function uniqueName(base: string, accounts: Account[]): string {
  const taken = new Set(accounts.map((a) => a.name.trim().toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let i = 2; ; i++) if (!taken.has(`${base} ${i}`.toLowerCase())) return `${base} ${i}`;
}

export function ConnectPanel({
  card,
  types,
  account,
  frame = true,
  onConnected,
}: {
  card: MarketplacePickerCard;
  types: { spot: boolean; futures: boolean };
  /** Edit mode: change the keys of this connection instead of creating one. */
  account?: Account;
  /** False inside a dialog: no card around the guide. */
  frame?: boolean;
  onConnected: (id: string) => void;
}) {
  const api = useApi(["accounts"]);
  const [marketType, setMarketType] = useState<"SPOT" | "FUTURES">(
    account ? (isFuturesAccount(account) ? "FUTURES" : "SPOT") : types.futures && !types.spot ? "FUTURES" : "SPOT"
  );
  const [specs, setSpecs] = useState<ParamSpec[]>([]);
  const [config, setConfig] = useState<Record<string, unknown>>(() => ({ ...(account?.config || {}) }));
  const [name, setName] = useState(account?.name || "");
  const [nameTouched, setNameTouched] = useState(Boolean(account));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (account) return;
    if (!types.spot && marketType === "SPOT") setMarketType("FUTURES");
    if (!types.futures && marketType === "FUTURES") setMarketType("SPOT");
  }, [types, marketType, account]);

  // Load this exchange's credential fields for the chosen market type.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const locked = { ...card.staticOptions, market_type: marketType };
    api
      .commands.getParams({ category: "account_provider", name: card.provider, config: locked })
      .then((raw: unknown) => {
        if (cancelled) return;
        const next = normalizeConfigurableParams(raw).specs;
        setSpecs(next);
        setConfig((prev) => {
          const out: Record<string, unknown> = { ...locked };
          for (const s of next) {
            if (prev[s.name] !== undefined) out[s.name] = prev[s.name];
            else if (s.default !== undefined && s.default !== null) out[s.name] = s.default;
          }
          return { ...out, ...locked };
        });
      })
      .catch((e: Error) => !cancelled && setError(e.message || "Couldn't load this exchange's fields"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, marketType, api.ready]);

  const defaultName = uniqueName(
    `${card.title}${marketType === "FUTURES" ? " Futures" : ""}`,
    api.accounts || []
  );
  useEffect(() => {
    if (!nameTouched) setName(defaultName);
  }, [defaultName, nameTouched]);

  const credentialSpecs = useMemo(
    () => specs.filter((s) => s.name !== "market_type" && s.name !== "network"),
    [specs]
  );
  const keySpec = specs.find((s) => s.type === "ed25519");
  // With a self-generated key the form asks for the API key in its own step.
  const extraSpecs = credentialSpecs.filter((s) => s !== keySpec && !HMAC_FIELDS.has(s.name));
  const keyUrl = apiKeyPageUrl(card.provider, card.staticOptions.network);
  const openLink = keyUrl && (
    <a
      href={keyUrl}
      target="_blank"
      rel="noreferrer"
      className="mt-1.5 flex w-fit items-center gap-1 text-[var(--primary)] no-underline"
    >
      Open {card.title} <ExternalLink size={11} />
    </a>
  );
  const permissions = (
    <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      <Perm ok>Read</Perm>
      <Perm ok>{marketType === "FUTURES" ? "Futures trading" : "Spot trading"}</Perm>
      <Perm ok={false}>Withdrawals: keep off</Perm>
    </div>
  );
  const ipTitle = SERVER_IPS.length > 1 ? "Restrict the key to these IPs" : "Restrict the key to this IP";
  const ipHint = SERVER_IPS.length > 1 ? "Add all of them, so only our servers can use the key." : "Only our server can then use the key.";
  const storedNote = "Stored encrypted. Only used to place orders for your live strategies.";

  async function save() {
    setError(null);
    const missing = missingCredentialMessage(specs, config);
    if (missing) return setError(missing);
    if (!name.trim()) return setError("Give the connection a name.");
    setSaving(true);
    try {
      if (account) {
        await api.commands.updateAccount({
          id: account.id,
          name: name.trim(),
          config: buildConfigPayload(config, specs),
          enabled: true,
        });
        await api.commands.restartAccount({ id: account.id });
        onConnected(account.id);
        return;
      }
      const created = (await api.commands.createAccount({
        provider: card.provider,
        name: name.trim(),
        description: "",
        config: buildConfigPayload(config, specs),
      })) as Account | null;
      if (!created?.id) throw new Error("The exchange connection wasn't created.");
      await api.commands.updateAccount({ id: created.id, enabled: true });
      onConnected(created.id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't connect");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Frame frame={frame}>
      <div className="flex items-center gap-2.5">
        <ExchangeMark provider={card.provider} />
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">
            {account ? `Change keys for ${account.name}` : `Connect ${card.title}`}
          </h2>
          <p className="m-0 text-[13px] text-[var(--text-muted)]">
            {account ? "Leave a field empty to keep the saved key." : "About 2 minutes"}
          </p>
        </div>
        {!account && types.spot && types.futures && (
          <div className="flex rounded-[9px] border border-[var(--border)] bg-[var(--bg-secondary)] p-0.5" role="group" aria-label="Market">
            {(["SPOT", "FUTURES"] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={marketType === m}
                onClick={() => setMarketType(m)}
                className={`rounded-[7px] px-2.5 py-0.5 text-xs ${
                  marketType === m ? "bg-[var(--primary-soft)] font-medium text-[var(--primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
              >
                {m === "SPOT" ? "Spot" : "Futures"}
              </button>
            ))}
          </div>
        )}
      </div>

      {keySpec ? (
        // Self-generated (Ed25519) key: our public key goes to the exchange first.
        <div className="flex flex-col">
          <Step n={1} title="Generate your key here">
            Click Generate Key and copy the public key that appears. The private key stays in this app.
            <div className="mt-2">
              <Ed25519KeyInput
                showGuide={false}
                value={config[keySpec.name]}
                onChange={(v) => setConfig((prev) => ({ ...prev, [keySpec.name]: v }))}
              />
            </div>
          </Step>
          <Step n={2} title={`Create a self-generated API key on ${card.title}`}>
            <ol className="m-0 mt-1 list-decimal space-y-1 pl-4">
              <li>
                Open API Management and click <b className="font-medium text-[var(--text-primary)]">Create API</b>.
              </li>
              <li>
                Choose <b className="font-medium text-[var(--text-primary)]">Self-generated</b> (not System-generated).
              </li>
              <li>Paste the public key from step 1 and confirm.</li>
            </ol>
            {openLink}
          </Step>
          <Step n={3} title="Set these permissions">
            {permissions}
          </Step>
          <Step n={4} title={ipTitle}>
            {ipHint}
            <ServerIps />
          </Step>
          <Step n={5} title={`Paste the API key ${card.title} shows`}>
            It's the API Key string on the {card.title} API page, not the public or private key.
            <div className="mt-2 flex flex-col gap-2">
              <input
                type="text"
                className="font-mono !text-xs"
                autoComplete="off"
                aria-label="API key"
                value={isRedacted(config.api_key) ? "" : String(config.api_key ?? "")}
                placeholder={isRedacted(config.api_key) ? "Leave blank to keep the saved key" : "API key"}
                onChange={(e) => setConfig((prev) => ({ ...prev, api_key: e.target.value }))}
              />
              {extraSpecs.length > 0 && (
                <DynamicForm specs={extraSpecs} values={config} onChange={setConfig} loading={loading} columns={1} keyGuide={false} />
              )}
              <div className="text-xs text-[var(--text-muted)]">{storedNote}</div>
            </div>
          </Step>
        </div>
      ) : (
        <div className="flex flex-col">
          <Step n={1} title={`Create an API key on ${card.title}`}>
            Open the API management page and create a new key.
            {openLink}
          </Step>
          <Step n={2} title="Set these permissions">
            {permissions}
          </Step>
          <Step n={3} title={ipTitle}>
            {ipHint}
            <ServerIps />
          </Step>
          <Step n={4} title="Paste the keys here">
            <div className="mt-2">
              <DynamicForm specs={credentialSpecs} values={config} onChange={setConfig} loading={loading} columns={1} keyGuide={false} />
              <div className="mt-1.5 text-xs text-[var(--text-muted)]">{storedNote}</div>
            </div>
          </Step>
        </div>
      )}

      {error && <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{error}</div>}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-[200px] flex-1 flex-col gap-1.5">
          <span className={fieldLabel}>Name</span>
          <input
            value={name}
            onChange={(e) => {
              setNameTouched(true);
              setName(e.target.value);
            }}
            placeholder="Connection name"
          />
        </label>
        <button type="button" className="btn btn-primary" disabled={saving || loading} onClick={() => void save()}>
          {saving ? (account ? "Saving…" : "Connecting…") : "Save"}
        </button>
      </div>
    </Frame>
  );
}

/** Live result of a new connection: waits for the exchange to answer. */
export function ConnectResult({ account, onDone, frame = true }: { account: Account; onDone: () => void; frame?: boolean }) {
  const api = useApi(["accounts"]);
  const view = accountStatusView(account.status, account.enabled);
  const balance = accountUsdBalance(api.balancesByAccount?.[account.id]);
  const ok = view.tone === "running";
  const bad = view.tone === "error";

  return (
    <Frame frame={frame}>
      <div className="flex items-center gap-2.5">
        <ExchangeMark provider={account.provider} />
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">{account.name}</h2>
          <p className="m-0 text-[13px] text-[var(--text-muted)]">{accountKindLabel(account)}</p>
        </div>
      </div>
      <div
        className="rounded-[12px] border p-3.5"
        style={{
          borderColor: `color-mix(in srgb, ${bad ? "var(--danger)" : ok ? "var(--success)" : "var(--border)"} 40%, var(--border))`,
          background: bad
            ? "color-mix(in srgb, var(--danger) 10%, transparent)"
            : ok
              ? "color-mix(in srgb, var(--success) 12%, transparent)"
              : "var(--bg-secondary)",
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <b className="text-[13px] text-[var(--text-primary)]">{ok ? "Connected" : bad ? "Couldn't connect" : "Checking the keys…"}</b>
          {ok ? <Pill tone="ok">Verified</Pill> : bad ? <Pill tone="bad">Error</Pill> : <Pill tone="info">Checking</Pill>}
        </div>
        {(ok || bad) && (
          <div className="mt-1.5 flex flex-col">
            {ok ? (
              <>
                {balance && <CheckRow ok title={`${money.format(balance.value)} ${balance.asset} available`} />}
                <CheckRow ok title="Keys accepted" hint="The exchange answered and the account is streaming." />
                <CheckRow ok title="Ready for live strategies" />
              </>
            ) : (
              <CheckRow
                ok={false}
                title={account.status_msg || "The exchange rejected the connection."}
                hint={`Check the key, its permissions, and that it allows ${SERVER_IPS.join(" and ")}.`}
              />
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {bad && (
          <Link to={`/integrations/marketplaces/${account.id}/edit`} className="btn btn-soft">
            Change keys
          </Link>
        )}
        {ok && (
          <Link to="/live/new" className="btn btn-primary">
            Go live →
          </Link>
        )}
        <button type="button" className="btn btn-ghost" onClick={onDone}>
          Done
        </button>
      </div>
    </Frame>
  );
}

